import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Snackbar,
  Autocomplete,
  Tooltip,
  Popover,
  InputAdornment
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Search as SearchIcon
} from '@mui/icons-material';

const ExcelEditor = () => {
  const [files, setFiles] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [originalRows, setOriginalRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  
  const [filters, setFilters] = useState({});
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [currentFilterColumn, setCurrentFilterColumn] = useState(null);
  const [filterValue, setFilterValue] = useState('');
  
  
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const originalValueRef = useRef(null);
  
  
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newRowData, setNewRowData] = useState([]);
  const [addingRow, setAddingRow] = useState(false);
  const [nextId, setNextId] = useState(null);
  
  
  const [backupsDialogOpen, setBackupsDialogOpen] = useState(false);
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  
  const API_BASE_URL = 'https://test2.planfix-to-syrve.com';
  
  
  const applyFilters = useCallback(() => {
    let filteredRows = [...originalRows];
    
    for (const [colIndex, filterValue] of Object.entries(filters)) {
      if (filterValue && filterValue.trim() !== '') {
        const searchTerm = filterValue.toLowerCase().trim();
        filteredRows = filteredRows.filter(row => {
          const cellValue = row[parseInt(colIndex)] || '';
          return cellValue.toString().toLowerCase().includes(searchTerm);
        });
      }
    }
    
    setRows(filteredRows);
  }, [filters, originalRows]);
  
  
  const clearAllFilters = useCallback(() => {
    setFilters({});
    setRows([...originalRows]);
    setFilterAnchorEl(null);
    setCurrentFilterColumn(null);
    setFilterValue('');
  }, [originalRows]);
  
  
  const clearColumnFilter = useCallback((colIndex) => {
    const newFilters = { ...filters };
    delete newFilters[colIndex];
    setFilters(newFilters);
  }, [filters]);
  
  
  const applyColumnFilter = useCallback(() => {
    if (currentFilterColumn !== null) {
      setFilters(prev => ({
        ...prev,
        [currentFilterColumn]: filterValue
      }));
      setFilterAnchorEl(null);
      setCurrentFilterColumn(null);
      setFilterValue('');
    }
  }, [currentFilterColumn, filterValue]);
  
  
  const openFilterPopover = (event, colIndex) => {
    setFilterAnchorEl(event.currentTarget);
    setCurrentFilterColumn(colIndex);
    setFilterValue(filters[colIndex] || '');
  };
  
  const closeFilterPopover = () => {
    setFilterAnchorEl(null);
    setCurrentFilterColumn(null);
    setFilterValue('');
  };
  
  
  const createBackup = useCallback(async (action) => {
    try {
      await axios.post(`${API_BASE_URL}/api/excel-files/create-backup`, {
        filename: selectedFile.filename,
        action: action
      });
      console.log(`Створено резервну копію перед дією: ${action}`);
    } catch (err) {
      console.error('Error creating backup:', err);
    }
  }, [selectedFile]);
  
  
  const shouldAutoGenerateId = useCallback(() => {
    if (!selectedFile) return false;
    
    const filesWithAutoId = [
      'Перелік систем',
      '2. Перелік систем',
      'Додатки + ціна',
      'Додаткова_комплектація_співвідношення'
    ];
    
    return filesWithAutoId.some(name => 
      selectedFile.filename?.includes(name) || selectedFile.name?.includes(name)
    );
  }, [selectedFile]);
  
  
  const saveEdit = useCallback(async () => {
    if (!editingCell) return;
    
    const { rowIndex, colIndex } = editingCell;
    const oldValue = originalValueRef.current;
    const newValue = editValue;
    
    if (oldValue === newValue) {
      setEditingCell(null);
      setEditValue('');
      originalValueRef.current = null;
      return;
    }
    
    try {
      await createBackup('edit_cell');
      
      const response = await axios.post(`${API_BASE_URL}/api/excel-files/update-cell`, {
        filename: selectedFile.filename,
        rowIndex: rowIndex,
        colIndex: colIndex,
        value: newValue
      });
      
      if (response.data.success) {
        setOriginalRows(prevRows => {
          const newRows = [...prevRows];
          if (newRows[rowIndex]) {
            newRows[rowIndex][colIndex] = newValue;
          }
          return newRows;
        });
        setRows(prevRows => {
          const newRows = [...prevRows];
          if (newRows[rowIndex]) {
            newRows[rowIndex][colIndex] = newValue;
          }
          return newRows;
        });
        setSuccess('Оновлено');
        setTimeout(() => setSuccess(''), 1500);
      } else {
        setError(response.data.error || 'Помилка оновлення');
      }
    } catch (err) {
      console.error('Error updating cell:', err);
      setError('Помилка при оновленні клітинки');
    } finally {
      setEditingCell(null);
      setEditValue('');
      originalValueRef.current = null;
    }
  }, [editingCell, editValue, selectedFile, createBackup]);
  
  
  const calculateNextId = useCallback(() => {
    if (!shouldAutoGenerateId()) return null;
    if (!originalRows || originalRows.length === 0) return 1;
    
    let maxId = 0;
    for (const row of originalRows) {
      const firstColValue = row[0];
      if (firstColValue && !isNaN(firstColValue)) {
        const numValue = parseInt(firstColValue, 10);
        if (!isNaN(numValue) && numValue > maxId) {
          maxId = numValue;
        }
      }
    }
    return maxId + 1;
  }, [originalRows, shouldAutoGenerateId]);
  
  
  useEffect(() => {
    if (addDialogOpen && shouldAutoGenerateId()) {
      const nextIdValue = calculateNextId();
      setNextId(nextIdValue);
      
      const newData = new Array(headers.length).fill('');
      if (headers.length > 0) {
        newData[0] = nextIdValue ? nextIdValue.toString() : '';
      }
      setNewRowData(newData);
    } else if (addDialogOpen && !shouldAutoGenerateId()) {
      setNextId(null);
      setNewRowData(new Array(headers.length).fill(''));
    }
  }, [addDialogOpen, headers, calculateNextId, shouldAutoGenerateId]);
  
  
  useEffect(() => {
    applyFilters();
  }, [filters, originalRows, applyFilters]);
  
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editingCell) {
        const target = event.target;
        const isEditingField = target.closest('.MuiAutocomplete-root') || 
                              target.closest('input') ||
                              target.closest('.Mui-focused');
        
        if (!isEditingField) {
          saveEdit();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editingCell, saveEdit]);
  
  useEffect(() => {
    fetchFiles();
  }, []);
  
  useEffect(() => {
    if (selectedFile) {
      loadFileData();
    }
  }, [selectedFile]);
  
  const fetchFiles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/excel-files/list`);
      if (response.data.success) {
        setFiles(response.data.data);
        if (response.data.data.length > 0) {
          setSelectedFile(response.data.data[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching files:', err);
      setError('Помилка завантаження списку файлів');
    } finally {
      setLoading(false);
    }
  };
  
  const loadFileData = async () => {
    if (!selectedFile) return;
    
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE_URL}/api/excel-files/load`, {
        filename: selectedFile.filename
      });
      
      if (response.data.success) {
        let loadedHeaders = response.data.data.headers || [];
        let loadedRows = response.data.data.rows || [];
        
        if (selectedFile.filename.includes('Коди назв систем') || selectedFile.name === 'Коди назв систем') {
          loadedHeaders = loadedHeaders.slice(0, 2);
          loadedRows = loadedRows.map(row => row.slice(0, 2));
        }
        
        if (selectedFile.filename.includes('Додаткова_комплектація_співвідношення') || 
            selectedFile.name === 'Додатки + ціна') {
          const nonEmptyIndices = [];
          for (let i = 0; i < loadedHeaders.length; i++) {
            const hasHeader = loadedHeaders[i] && loadedHeaders[i] !== '';
            const hasData = loadedRows.some(row => row[i] && row[i].toString().trim());
            if (hasHeader || hasData) {
              nonEmptyIndices.push(i);
            }
          }
          loadedHeaders = nonEmptyIndices.map(i => loadedHeaders[i]);
          loadedRows = loadedRows.map(row => nonEmptyIndices.map(i => row[i] || ''));
        }
        
        setHeaders(loadedHeaders);
        setOriginalRows(loadedRows);
        setRows(loadedRows);
        setFilters({});
      } else {
        setError(response.data.error || 'Помилка завантаження даних');
      }
    } catch (err) {
      console.error('Error loading file:', err);
      setError('Помилка при завантаженні файлу');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    try {
      setSaving(true);
      await createBackup('before_save');
      
      const response = await axios.post(`${API_BASE_URL}/api/excel-files/save`, {
        filename: selectedFile.filename,
        headers: headers,
        rows: originalRows
      });
      
      if (response.data.success) {
        setSuccess('Дані успішно збережено');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.error || 'Помилка збереження');
      }
    } catch (err) {
      console.error('Error saving:', err);
      setError('Помилка при збереженні');
    } finally {
      setSaving(false);
    }
  };
  
  const startEditing = useCallback((rowIndex, colIndex, currentValue) => {
    if (editingCell) return;
    originalValueRef.current = currentValue || '';
    setEditingCell({ rowIndex, colIndex });
    setEditValue(currentValue || '');
  }, [editingCell]);
  
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
      originalValueRef.current = null;
    }
  }, [saveEdit]);
  
  const handleAddRow = async () => {
    const isEmpty = newRowData.some((cell, idx) => {
      if (shouldAutoGenerateId() && idx === 0) return false;
      return !cell || cell === '';
    });
    
    if (isEmpty) {
      setError('Будь ласка, заповніть всі поля');
      return;
    }
    
    try {
      setAddingRow(true);
      await createBackup('add_row');
      
      const response = await axios.post(`${API_BASE_URL}/api/excel-files/add-row`, {
        filename: selectedFile.filename,
        headers: headers,
        rowData: newRowData
      });
      
      if (response.data.success) {
        setSuccess('Рядок успішно додано');
        await loadFileData();
        setAddDialogOpen(false);
        setNewRowData([]);
        setNextId(null);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.error || 'Помилка додавання рядка');
      }
    } catch (err) {
      console.error('Error adding row:', err);
      setError('Помилка при додаванні рядка');
    } finally {
      setAddingRow(false);
    }
  };
  
  const handleDeleteRow = useCallback(async (rowIndex) => {
    
    const deletedRow = rows[rowIndex];
    const originalIndex = originalRows.findIndex(row => 
      JSON.stringify(row) === JSON.stringify(deletedRow)
    );
    
    if (window.confirm('Ви впевнені, що хочете видалити цей рядок?')) {
      try {
        await createBackup('delete_row');
        
        const response = await axios.post(`${API_BASE_URL}/api/excel-files/delete-row`, {
          filename: selectedFile.filename,
          rowIndex: originalIndex >= 0 ? originalIndex : rowIndex
        });
        
        if (response.data.success) {
          setSuccess('Рядок видалено');
          await loadFileData();
          setTimeout(() => setSuccess(''), 2000);
        } else {
          setError(response.data.error || 'Помилка видалення рядка');
        }
      } catch (err) {
        console.error('Error deleting row:', err);
        setError('Помилка при видаленні рядка');
      }
    }
  }, [rows, originalRows, selectedFile, createBackup]);
  
  const fetchBackups = useCallback(async () => {
    try {
      setLoadingBackups(true);
      const response = await axios.get(`${API_BASE_URL}/api/excel-files/backups/${selectedFile.filename}`);
      if (response.data.success) {
        setBackups(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching backups:', err);
      setError('Помилка завантаження резервних копій');
    } finally {
      setLoadingBackups(false);
    }
  }, [selectedFile]);
  
  const handleRestoreBackup = useCallback(async (backupName) => {
    if (window.confirm(`Відновити з резервної копії ${backupName}? Поточні зміни будуть втрачені.`)) {
      try {
        const response = await axios.post(`${API_BASE_URL}/api/excel-files/restore`, {
          filename: selectedFile.filename,
          backupName: backupName
        });
        
        if (response.data.success) {
          setSuccess('Файл відновлено з резервної копії');
          await loadFileData();
          setBackupsDialogOpen(false);
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError(response.data.error || 'Помилка відновлення');
        }
      } catch (err) {
        console.error('Error restoring:', err);
        setError('Помилка при відновленні');
      }
    }
  }, [selectedFile]);
  
  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
    setSelectedFile(files[newValue]);
    setEditingCell(null);
    setFilters({});
  }, [files]);
  
  const openAddDialog = useCallback(() => {
    setAddDialogOpen(true);
  }, []);
  
  const openBackupsDialog = useCallback(() => {
    fetchBackups();
    setBackupsDialogOpen(true);
  }, [fetchBackups]);
  
  
  const activeFiltersCount = Object.keys(filters).filter(key => filters[key] && filters[key].trim() !== '').length;
  
  
  const tableRows = useMemo(() => {
    if (rows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={headers.length + 1} align="center">
            <Typography color="textSecondary">Немає даних</Typography>
          </TableCell>
        </TableRow>
      );
    }
    
    return rows.map((row, rowIdx) => (
      <TableRow key={rowIdx} hover>
        {headers.map((_, colIdx) => {
          const isEditing = editingCell?.rowIndex === rowIdx && editingCell?.colIndex === colIdx;
          const cellValue = row[colIdx] || '';
          
          return (
            <TableCell 
              key={colIdx}
              onClick={() => !isEditing && startEditing(rowIdx, colIdx, cellValue)}
              onDoubleClick={() => !isEditing && startEditing(rowIdx, colIdx, cellValue)}
              sx={{ 
                cursor: 'pointer', 
                '&:hover': { backgroundColor: '#e3f2fd' },
                backgroundColor: isEditing ? '#fff3e0' : 'inherit',
                padding: '8px 12px',
                minWidth: 120,
                maxWidth: 300,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={cellValue}
            >
              {isEditing ? (
                <TextField
                  autoFocus
                  size="small"
                  variant="standard"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={saveEdit}
                  sx={{ width: '100%', minWidth: 120 }}
                  placeholder="Введіть значення..."
                />
              ) : (
                cellValue
              )}
            </TableCell>
          );
        })}
        <TableCell align="center" sx={{ padding: '8px 4px' }}>
          <Tooltip title="Видалити рядок">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteRow(rowIdx)}
              disabled={!!editingCell}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    ));
  }, [rows, headers, editingCell, editValue, startEditing, handleKeyDown, saveEdit, handleDeleteRow]);
  
  if (loading && !selectedFile) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" sx={{ mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  const isFilterOpen = Boolean(filterAnchorEl);
  
  return (
    <Box sx={{ p: 2, maxWidth: '100%', margin: '0 auto', mt: 8 }}>
  
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      <Snackbar 
        open={!!success} 
        autoHideDuration={2000} 
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
      
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {files.map((file, index) => (
            <Tab key={file.id} label={file.name} />
          ))}
        </Tabs>
      </Paper>
      
      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || !!editingCell}
          size="small"
        >
          {saving ? 'Збереження...' : 'Зберегти'}
        </Button>
        <Button
          variant="outlined"
          color="success"
          startIcon={<AddIcon />}
          onClick={openAddDialog}
          disabled={!!editingCell}
          size="small"
        >
          Додати рядок
        </Button>
        <Button
          variant="outlined"
          color="warning"
          startIcon={<HistoryIcon />}
          onClick={openBackupsDialog}
          size="small"
        >
          Резервні копії
        </Button>
        {activeFiltersCount > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<ClearIcon />}
            onClick={clearAllFilters}
            size="small"
          >
            Очистити всі фільтри ({activeFiltersCount})
          </Button>
        )}
        <Chip 
          label={`Всього записів: ${rows.length}${originalRows.length !== rows.length ? ` (з ${originalRows.length})` : ''}`} 
          color="primary" 
          variant="outlined"
          size="small"
        />
        {shouldAutoGenerateId() && nextId && (
          <Chip 
            label={`Наступний ID: ${nextId}`} 
            color="info" 
            size="small"
          />
        )}
        {editingCell && (
          <Chip 
            label="Редагування..." 
            color="warning" 
            size="small"
          />
        )}
      </Paper>
      
      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 280px)' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              {headers.map((header, idx) => {
                const hasFilter = filters[idx] && filters[idx].trim() !== '';
                
                return (
                  <TableCell 
                    key={idx} 
                    sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: '#f5f5f5', 
                      minWidth: 120,
                      padding: '8px 12px'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <span>{header || `Колонка ${idx + 1}`}</span>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title={hasFilter ? `Фільтр: ${filters[idx]}` : 'Фільтрувати'}>
                          <IconButton
                            size="small"
                            onClick={(e) => openFilterPopover(e, idx)}
                            sx={{ 
                              p: 0.5,
                              color: hasFilter ? 'primary.main' : 'grey.500'
                            }}
                          >
                            <FilterIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {hasFilter && (
                          <Tooltip title="Очистити фільтр">
                            <IconButton
                              size="small"
                              onClick={() => clearColumnFilter(idx)}
                              sx={{ p: 0.5, color: 'error.main' }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                );
              })}
              <TableCell 
                align="center" 
                sx={{ 
                  fontWeight: 'bold', 
                  backgroundColor: '#f5f5f5', 
                  width: 80, 
                  padding: '8px 4px' 
                }}
              >
                Дії
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={headers.length + 1} align="center">
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : (
              tableRows
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {}
      <Popover
        open={isFilterOpen}
        anchorEl={filterAnchorEl}
        onClose={closeFilterPopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Typography variant="subtitle2" gutterBottom>
            Фільтр для колонки: {currentFilterColumn !== null ? (headers[currentFilterColumn] || `Колонка ${currentFilterColumn + 1}`) : ''}
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Значення для пошуку"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                applyColumnFilter();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            autoFocus
          />
          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={closeFilterPopover}>
              Скасувати
            </Button>
            <Button size="small" variant="contained" onClick={applyColumnFilter}>
              Застосувати
            </Button>
          </Box>
        </Box>
      </Popover>
      
      {}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Додати новий рядок
          <IconButton onClick={() => setAddDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {headers.map((header, idx) => {
              const isFirstColumn = idx === 0;
              const isAutoId = shouldAutoGenerateId() && isFirstColumn;
              const columnValues = [...new Set(originalRows.map(row => row[idx]).filter(v => v && v !== ''))];
              
              return (
                <Autocomplete
                  key={idx}
                  freeSolo
                  options={columnValues}
                  value={newRowData[idx] || ''}
                  onChange={(event, newValue) => {
                    const newData = [...newRowData];
                    newData[idx] = newValue || '';
                    setNewRowData(newData);
                  }}
                  onInputChange={(event, newValue) => {
                    const newData = [...newRowData];
                    newData[idx] = newValue || '';
                    setNewRowData(newData);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label={header || `Колонка ${idx + 1}`}
                      size="small"
                      variant="outlined"
                      disabled={addingRow || (isAutoId && nextId !== null)}
                      autoFocus={idx === (shouldAutoGenerateId() ? 1 : 0)}
                      placeholder={isAutoId && nextId ? `Наступний ID: ${nextId}` : ''}
                      helperText={isAutoId && nextId ? `Автоматично згенерований ID` : ''}
                      InputProps={{
                        ...params.InputProps,
                        readOnly: isAutoId && nextId !== null,
                      }}
                    />
                  )}
                  size="small"
                  disablePortal
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={addingRow}>
            Скасувати
          </Button>
          <Button onClick={handleAddRow} variant="contained" color="primary" disabled={addingRow}>
            {addingRow ? 'Додавання...' : 'Додати'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {}
      <Dialog open={backupsDialogOpen} onClose={() => setBackupsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Резервні копії
          <IconButton onClick={() => setBackupsDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loadingBackups ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={30} />
            </Box>
          ) : backups.length === 0 ? (
            <Typography textAlign="center" py={4} color="textSecondary">
              Немає резервних копій
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Назва копії</TableCell>
                    <TableCell>Дата</TableCell>
                    <TableCell align="center">Дії</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {backups.map((backup, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ wordBreak: 'break-all' }}>{backup.name}</TableCell>
                      <TableCell>{new Date(backup.date).toLocaleString('uk-UA')}</TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          startIcon={<RestoreIcon />}
                          onClick={() => handleRestoreBackup(backup.name)}
                        >
                          Відновити
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ExcelEditor;
